
import { useState } from 'react';
import Button from '../../../components/base/Button';

interface BookingConfirmationProps {
  bookingData: {
    workshopTitle: string;
    format: string;
    price: number;
    date: string;
    name: string;
    email: string;
  };
  onClose: () => void;
}

export default function BookingConfirmation({ bookingData, onClose }: BookingConfirmationProps) {
  const [showPaymentOptions, setShowPaymentOptions] = useState(false);

  const handleScheduleCall = () => {
    // Trigger Readdy Agent widget
    const widget = document.querySelector('#vapi-widget-floating-button') as HTMLElement;
    if (widget) {
      widget.click();
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg p-8 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="text-center mb-6">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <i className="ri-check-line text-3xl text-green-600"></i>
          </div>
          <h2 className="text-3xl font-bold text-slate-800 mb-2">Booking Request Submitted!</h2>
          <p className="text-slate-600">Thank you for your interest in our workshop experience.</p>
        </div>

        {/* Booking Summary */}
        <div className="bg-slate-50 rounded-lg p-6 mb-6">
          <h3 className="font-bold text-slate-800 mb-4">Booking Summary</h3>
          <div className="space-y-3">
            <div className="flex justify-between">
              <span className="text-slate-600">Workshop:</span>
              <span className="font-semibold text-slate-800">{bookingData.workshopTitle}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-slate-600">Format:</span>
              <span className="font-semibold text-slate-800">{bookingData.format}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-slate-600">Preferred Date:</span>
              <span className="font-semibold text-slate-800">{bookingData.date}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-slate-600">Participant:</span>
              <span className="font-semibold text-slate-800">{bookingData.name}</span>
            </div>
            <div className="border-t pt-3 mt-3">
              <div className="flex justify-between text-lg">
                <span className="font-bold text-slate-800">Total Investment:</span>
                <span className="font-bold text-blue-600">${bookingData.price}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Next Steps */}
        <div className="mb-6">
          <h3 className="font-bold text-slate-800 mb-4">What Happens Next?</h3>
          <div className="space-y-4">
            <div className="flex items-start space-x-3">
              <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                <span className="text-sm font-bold text-blue-600">1</span>
              </div>
              <div>
                <h4 className="font-semibold text-slate-800">Confirmation Call</h4>
                <p className="text-slate-600 text-sm">I'll contact you within 24 hours to confirm details and answer any questions.</p>
              </div>
            </div>
            <div className="flex items-start space-x-3">
              <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                <span className="text-sm font-bold text-blue-600">2</span>
              </div>
              <div>
                <h4 className="font-semibold text-slate-800">Schedule & Payment</h4>
                <p className="text-slate-600 text-sm">We'll finalize your workshop date and arrange secure payment.</p>
              </div>
            </div>
            <div className="flex items-start space-x-3">
              <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                <span className="text-sm font-bold text-blue-600">3</span>
              </div>
              <div>
                <h4 className="font-semibold text-slate-800">Pre-Workshop Materials</h4>
                <p className="text-slate-600 text-sm">You'll receive preparation materials and joining instructions.</p>
              </div>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="space-y-4">
          <Button 
            onClick={handleScheduleCall}
            className="w-full bg-blue-800 hover:bg-blue-900"
            size="lg"
          >
            <i className="ri-phone-line mr-2"></i>
            Schedule Confirmation Call Now
          </Button>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Button 
              onClick={() => setShowPaymentOptions(!showPaymentOptions)}
              variant="outline"
              className="border-green-600 text-green-600 hover:bg-green-600 hover:text-white"
            >
              <i className="ri-secure-payment-line mr-2"></i>
              Payment Options
            </Button>
            <Button 
              onClick={onClose}
              variant="outline"
              className="border-slate-400 text-slate-600 hover:bg-slate-100"
            >
              Close
            </Button>
          </div>
        </div>

        {/* Payment Options */}
        {showPaymentOptions && (
          <div className="mt-6 p-4 bg-green-50 border border-green-200 rounded-lg">
            <h4 className="font-semibold text-green-800 mb-3">Secure Payment Options</h4>
            <div className="space-y-2 text-sm text-green-700">
              <div className="flex items-center">
                <i className="ri-bank-card-line mr-2"></i>
                <span>Credit/Debit Cards (Visa, MasterCard, Amex)</span>
              </div>
              <div className="flex items-center">
                <i className="ri-paypal-line mr-2"></i>
                <span>PayPal & Digital Wallets</span>
              </div>
              <div className="flex items-center">
                <i className="ri-bank-line mr-2"></i>
                <span>Bank Transfer (for corporate bookings)</span>
              </div>
              <div className="flex items-center">
                <i className="ri-calendar-check-line mr-2"></i>
                <span>Payment plans available for group bookings</span>
              </div>
            </div>
            <p className="text-xs text-green-600 mt-3">
              All payments are processed securely. No payment is required until we confirm your booking.
            </p>
          </div>
        )}

        {/* Contact Info */}
        <div className="mt-6 p-4 bg-slate-50 rounded-lg">
          <h4 className="font-semibold text-slate-800 mb-2">Questions?</h4>
          <p className="text-sm text-slate-600 mb-2">
            Feel free to reach out if you have any questions about your booking.
          </p>
          <div className="flex items-center text-sm text-slate-600">
            <i className="ri-mail-line mr-2"></i>
            <span>hello@elbertcalexander.com</span>
          </div>
        </div>
      </div>
    </div>
  );
}
